/* -------------------------- Design imports start -------------------------- */
/* --------------------------- Design imports end --------------------------- */
/* ------------------------ Functional imports start ------------------------ */
import { DeleteOutlined, EditOutlined } from '@mui/icons-material'
import { Stack } from '@mui/material'
import React, { useCallback, useState } from 'react'
import { flushSync } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'

import { canDelete, isPrivileged } from '../../../utils/functions'
import DeleteModal from '../../../components/widgets/DeleteModal'

import { toast } from 'react-toastify'

import { FilesDrawer } from '../../Filesystem'
import OfferDrawer from './CreateOfferDrawer'
import OfferStaffDrawer from './OfferStaffDrawer'
import { OfferInfoPanelContent } from './OfferInfoPanel'
import { type Offer } from '../utils/types'
import ContractDrawer from '../../Contract/Contract/ContractDrawer'
import Button from '../../../components/Button'
import SmartTable from '../../../components/SmartTable'
import LogTool from '../../../logger/logTools'
import { ContractTabType, useCountContext, useUserContext } from '../../../utils/context'
import { type FileInfo, type SmartTableColumnType } from '../../../utils/types'
import GetConstant from '../utils/constants'
import { useOffers } from '../hooks/useRequestQueries'
import { useDeleteOffers, usePatchOffer } from '../hooks/useRequestMutations'
import { useCreateContract } from '../../Contract/hooks/useContractMutations'
import { offerKeys } from '../utils/queryKeys'
import { markEntityAsRead } from '../../../api/unreadOverview'
import { PERMISSIONS } from '../../../config/permissions'
import { type OfferFilters } from '../api/requestApi'
import { deriveFilesUrl } from '../../Filesystem/utils/filesHelper'
/* ------------------------- Functional imports end ------------------------- */

type Props = {
  selectedOffer: Offer | undefined
  setSelectedOffer: React.Dispatch<React.SetStateAction<Offer | undefined>>
  openFilesDrawer: boolean
  setOpenFilesDrawer: React.Dispatch<React.SetStateAction<boolean>>
}

/* -------------------------------------------------------------------------- */
/*                                Start Component                             */
/* -------------------------------------------------------------------------- */
export default function OfferOverviewPage(props: Props) {
  /* -------------------------- Non state data start -------------------------- */
  const log = new LogTool({ context: 'OfferOverviewPage', enable: true, logLevel: 'warn' })
  const { t } = useTranslation(['request', 'common'])
  const {
    selectedOffer,
    setSelectedOffer,
    openFilesDrawer,
    setOpenFilesDrawer,
  } = props
  const queryClient = useQueryClient()
  const { user, userPermissions } = useUserContext()
  const {
    initialOffers,
    setInitialOffers,
    setInitialOffersCount,
    setCountTotals,
    offerQueryOptions,
    setOfferQueryOptions,
  } = useCountContext()
  
  // React Query Hooks
  const patchOfferMutation = usePatchOffer()
  const deleteOffersMutation = useDeleteOffers()
  const createContractMutation = useCreateContract()
  /* --------------------------- Non state data end --------------------------- */

  /* ---------------------------- Flag states start --------------------------- */
  const [offerDrawerOpen, setOfferDrawerOpen] = useState<boolean>(false)
  const [offerStaffDrawerOpen, setOfferStaffDrawerOpen] = useState<boolean>(false)
  const [contractDrawerOpen, setContractDrawerOpen] = useState<boolean>(false)
  const [openDelete, setOpenDelete] = useState(false)
  const [filesDrawerRefreshKey, setFilesDrawerRefreshKey] = useState(0)
  /* ----------------------------- Flag states end ---------------------------- */

  /* ---------------------------- Data states start --------------------------- */
  const [selectedOffers, setSelectedOffers] = useState<Offer[]>([])
  const [offerFormInput, setOfferFormInput] = useState<any>({})
  const [offerStaffFormInput, setOfferStaffFormInput] = useState<any>({})
  const [contractFormInput, setContractFormInput] = useState<any>({})
  const [newOffers, setNewOffers] = useState<any[]>(initialOffers)
  /* ----------------------------- Data states end ---------------------------- */

  /* ------------------------ Callback functions start ------------------------ */

  async function markOfferAsRead(offerItem: Offer): Promise<void> {
    const offerId = Number(offerItem.key)
    if (Number.isNaN(offerId)) {
      log.warn('Unable to resolve offer id for marking as read', offerItem)
      return
    }

    // Optimistic update directly on all offer list queries
    queryClient.setQueriesData(
      { queryKey: offerKeys.lists(), exact: false },
      (oldData: any) => {
        if (!oldData || !oldData.results) return oldData
        return {
          ...oldData,
          results: oldData.results.map((item: Offer) =>
            item.key === offerItem.key ? { ...item, read: true } : item
          )
        }
      }
    )

    // Update counts for tab badges
    setInitialOffersCount(prev => Math.max(0, prev - 1))
    setCountTotals(prev => Math.max(0, prev - 1))

    try {
      await markEntityAsRead('offer', offerId)
    } catch (error) {
      log.error('Error marking offer as read', error)
      toast.error(t('request:feedback.error.markOfferAsRead', 'Fehler beim Aktualisieren des Lesestatus'))
    }
  }

  /**
   * Refreshes the data source by fetching the offers from the API
   */
  const refreshDataSource = useCallback(async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: offerKeys.lists() })
  }, [queryClient])

  async function handleUpdateOffer(input: any): Promise<string> {
    log.debug('Updating offer -> ', input)
    if (!input?.self) {
      throw new Error('Error updating offer: missing offer identifier')
    }

    try {
      const updatedOffer = await patchOfferMutation.mutateAsync({
        idOrSelf: input.self,
        data: input,
      })

      toast.success(t('request:feedback.success.updateRequest'))
      setSelectedOffers([])
      setSelectedOffer(undefined)
      // Don't set refreshKey here - it will be set after files are uploaded via onFilesUploaded callback

      return typeof updatedOffer.files === 'string' ? updatedOffer.files : ''
    } catch (error) {
      log.error('Error updating offer -> ', error)
      toast.error(t('request:feedback.error.updateRequest'))
      setSelectedOffers([])
      setSelectedOffer(undefined)
      return ''
    }
  }

  const handleFilesUploaded = () => {
    // Trigger refresh of FilesDrawer after files are uploaded
    setFilesDrawerRefreshKey(prev => prev + 1)
  }

  const handleCreateContract = useCallback(async (contractInput: any): Promise<string | undefined> => {
    log.debug('Creating contract -> ', contractInput)
    
    try {
      const createdContract = await createContractMutation.mutateAsync({
        ...contractInput,
        status: 0,
      })
      
      log.info('Success creating contract', createdContract)
      toast.success(t('contract:feedback.success.contractCreated'))
      await refreshDataSource()
      
      return typeof createdContract.files === 'string' ? createdContract.files : undefined
    } catch (error) {
      log.error('Error creating contract', error)
      toast.error(t('contract:feedback.error.contractNotCreated'))
      return undefined
    }
  }, [log, createContractMutation, t, refreshDataSource])

  const handleContract = useCallback(async (event: any, selectedOffer: Offer) => {
    log.debug('Creating Contract for offer -> ', selectedOffer)
    event.stopPropagation()
    event.preventDefault()
    const newContract: any = {
      offer: selectedOffer,
    }
    setContractFormInput(newContract)
    setContractDrawerOpen(true)
    await refreshDataSource()
  }, [log, refreshDataSource])

  const handleDeclineOffer = useCallback(async (event: any, selectedOffer: Offer): Promise<void> => {
    log.debug('Declining offer -> ', selectedOffer)
    event.stopPropagation()
    event.preventDefault()
    try {
      await patchOfferMutation.mutateAsync({
        idOrSelf: selectedOffer.self,
        data: { status: 2 },
      })
      await refreshDataSource()
      toast.success(t('request:feedback.success.declineOffer'))
    } catch (error) {
      log.error('Error declining offer -> ', error)
      toast.error(t('request:feedback.error.declineOffer'))
    }
  }, [log, patchOfferMutation, refreshDataSource, t])

  /* ------------------------- Callback functions end ------------------------- */

  /* ------------------------- Render constants start ------------------------- */
  // GetConstant calls hooks internally, so it cannot be inside useMemo
  const offerTableColumns = GetConstant({
    name: 'offerTableColumns',
    newOffers: newOffers,
    handleContract: (e, record) => void (async () => await handleContract(e, record))(),
    handleDecline: (e, record) => void (async () => await handleDeclineOffer(e, record))(),
  }) as SmartTableColumnType<Offer>[]

  const offerTableActions = (
    <Stack direction="row" spacing={1} alignItems="center">
      {isPrivileged(user, 'STAFF') && (
        <Button
          id="editOfferButton"
          size="small"
          variant="outlined"
          color="primary"
          startIcon={<EditOutlined />}
          onClick={() => {
            if (isPrivileged(user, 'STAFF')) {
              setOfferStaffFormInput(selectedOffers[0])
              setOfferStaffDrawerOpen(true)
            } else {
              setOfferFormInput(selectedOffers[0])
              setOfferDrawerOpen(true)
            }
          }}
          disabled={selectedOffers.length !== 1}
        >
          {t('request:interaction.button.editOffer')}
        </Button>
      )}
      {(canDelete(userPermissions, PERMISSIONS.REQUEST.OFFER) || isPrivileged(user, 'STAFF')) && (
        <Button
          id="deleteOfferButton"
          size="small"
          variant="outlined"
          color="primary"
          startIcon={<DeleteOutlined />}
          onClick={() => setOpenDelete(true)}
          disabled={
            selectedOffers.length === 0 || selectedOffers.some(offer => offer.status !== 2)
          }
        >
          {t('request:interaction.button.deleteOffers')}
        </Button>
      )}
    </Stack>
  )
  /* -------------------------- Render constants end -------------------------- */

  /* ------------------------ Pre render actions start ------------------------ */
  let offerFiles: FileInfo[] | string = []
  if (selectedOffer) {
    if (typeof selectedOffer.files === 'string') {
      offerFiles = selectedOffer.files
    } else if (Array.isArray(selectedOffer.files) && selectedOffer.files.length > 0) {
      offerFiles = selectedOffer.files
    } else {
      const filesUrl = deriveFilesUrl(selectedOffer)
      if (filesUrl) {
        offerFiles = filesUrl
      }
    }
  }
  /* ------------------------- Pre render actions end ------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <>
      <SmartTable<Offer, OfferFilters>
        tableId="offerTable"
        columnsConfig={{
          allColumns: offerTableColumns,
          initialVisibleColumnIndices: [
            'self',
            'request',
            'article',
            'prices',
            'status',
            'editors',
            'action',
          ],
        }}
        queryConfig={{
          dataHook: useOffers,
          initialQueryOptions: offerQueryOptions,
        }}
        queryState={{
          options: offerQueryOptions,
          setOptions: setOfferQueryOptions,
        }}
        selection={{
          state: selectedOffers,
          setState: setSelectedOffers,
          onChange: (selected) => {
            if (selected.length === 1) {
              const record = selected[0]
              // Mark as read if unread
              if (!record.read) {
                void markOfferAsRead(record)
              }
              setSelectedOffer(record)
            } else if (selected.length === 0) {
              setSelectedOffer(undefined)
            }
          },
        }}
        actions={offerTableActions}
        customInfoPanel={(open, setOpen) => (
          <OfferInfoPanelContent
            setOpenFilesDrawer={setOpenFilesDrawer}
            infoData={selectedOffer}
          />
        )}
        onRow={{
          onClick: (record) => {
            setSelectedOffers([record])
            setSelectedOffer(record)
          },
        }}
        contextMenuOptions={[
          {
            label: t('common:interaction.button.showFiles'),
            onClick: () => setOpenFilesDrawer(true),
          },
        ]}
      />
      <OfferDrawer
        open={offerDrawerOpen}
        setOpen={setOfferDrawerOpen}
        formState={{
          state: offerFormInput,
          setState: setOfferFormInput,
        }}
        /* onCreateConfirm={handleCreateOffer} */
        enableEditMode={true}
      />
      <ContractDrawer
        open={contractDrawerOpen}
        setOpen={setContractDrawerOpen}
        formState={{
          state: contractFormInput,
          setState: setContractFormInput,
        }}
        enableEditMode={false}
        onCreateConfirm={handleCreateContract}
      />
      {isPrivileged(user, 'STAFF') && (
        <OfferStaffDrawer
          open={offerStaffDrawerOpen}
          setOpen={setOfferStaffDrawerOpen}
          formState={{
            state: offerStaffFormInput,
            setState: setOfferStaffFormInput,
          }}
          onEditConfirm={handleUpdateOffer}
          onFilesUploaded={handleFilesUploaded}
        />
      )}
      <DeleteModal
        open={openDelete}
        setOpen={setOpenDelete}
        onDelete={async () => {
          if (selectedOffers.length === 0) {
            setOpenDelete(false)
            return
          }

          const offersToDelete = [...selectedOffers]

          // Clear selection before mutation to prevent warning about selected items
          // that are no longer visible (mutation's onSuccess invalidates queries)
          flushSync(() => {
            setSelectedOffers([])
            setSelectedOffer(undefined)
            setOpenDelete(false)
          })

          try {
            await deleteOffersMutation.mutateAsync(offersToDelete)
            toast.success(t('request:feedback.success.deleteOffers'))
          } catch (error) {
            log.error('Error deleting offers -> ', error)
            toast.error(t('request:feedback.error.deleteOffers', 'Error deleting offers'))
          }
        }}
        name={t('common:content.label.offers')}
      />
      <FilesDrawer
        open={openFilesDrawer}
        setOpen={setOpenFilesDrawer}
        /* eslint-disable-next-line @typescript-eslint/no-empty-function */
        onClose={() => {}}
        files={offerFiles}
        title={t('common:content.label.offerFiles')}
        refreshKey={filesDrawerRefreshKey}
      />
    </>
  )
}
